import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const { user } = useAuth();

  const refresh = useCallback(async () => {
    if (!user) { setItems([]); return; }
    try {
      const { data } = await api.get("/cart");
      setItems(data);
    } catch {
      setItems([]);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const addItem = async (product_id, quantity = 1) => {
    const existing = items.find((i) => i.product.product_id === product_id);
    const qty = (existing?.quantity || 0) + quantity;
    await api.post("/cart/items", { product_id, quantity: qty });
    await refresh();
  };

  const updateItem = async (product_id, quantity) => {
    if (quantity <= 0) return removeItem(product_id);
    await api.post("/cart/items", { product_id, quantity });
    await refresh();
  };

  const removeItem = async (product_id) => {
    await api.delete(`/cart/items/${product_id}`);
    await refresh();
  };

  const subtotal = items.reduce((s, i) => s + i.product.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateItem, removeItem, refresh, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
};
